import { useState, useEffect } from 'react'
import {
	Box,
	Typography,
	Button,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	Alert,
	CircularProgress,
	IconButton,
	Collapse,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useApiKeys } from '../hooks/use-api-keys'
import { useApiConfig } from '../hooks/use-api-config'
import { supabase } from '../lib/supabase'

function DeveloperTab({ customerId }) {
	const { keys, loading, error, createKey, revokeKey } = useApiKeys(customerId)
	const { max_time_range_hours } = useApiConfig(customerId)
	const [customerConfig, setCustomerConfig] = useState(null)
	const [configLoading, setConfigLoading] = useState(true)

	// Format time limit for display
	const formatTimeLimit = (hours) => {
		if (hours >= 24) {
			return `${hours / 24} days`
		}
		return `${hours} hours`
	}

	// Create key form state
	const [showCreateForm, setShowCreateForm] = useState(false)
	const [keyName, setKeyName] = useState('')
	const [keyExpiry, setKeyExpiry] = useState('90d')
	const [creating, setCreating] = useState(false)
	const [createError, setCreateError] = useState(null)

	// Newly created key display
	const [newKey, setNewKey] = useState(null)
	const [copied, setCopied] = useState(false)

	// Fetch customer config for read-only display
	useEffect(() => {
		if (!customerId) return
		const fetchConfig = async () => {
			const { data, error } = await supabase
				.from('customers')
				.select('webhook_url, webhook_api_key, webhook_enabled')
				.eq('id', customerId)
				.single()
			if (!error && data) setCustomerConfig(data)
			setConfigLoading(false)
		}
		fetchConfig()
	}, [customerId])

	const handleCreateKey = async () => {
		if (!keyName.trim()) return
		setCreating(true)
		setCreateError(null)
		try {
			let expiresAt = null
			if (keyExpiry !== 'never') {
				const days = keyExpiry === '30d' ? 30 : keyExpiry === '90d' ? 90 : 365
				expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
			}
			const fullKey = await createKey(keyName.trim(), expiresAt)
			setNewKey(fullKey)
			setKeyName('')
			setShowCreateForm(false)
		} catch (err) {
			setCreateError(err.message)
		} finally {
			setCreating(false)
		}
	}

	const handleCopy = (text) => {
		navigator.clipboard.writeText(text)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	const handleRevoke = async (keyId) => {
		try {
			await revokeKey(keyId)
		} catch (err) {
			console.error('Error revoking key:', err)
		}
	}

	const getKeyStatus = (key) => {
		if (key.revoked_at) return { label: 'Revoked', color: 'error' }
		if (key.expires_at && new Date(key.expires_at) < new Date()) return { label: 'Expired', color: 'warning' }
		return { label: 'Active', color: 'success' }
	}

	const formatDate = (dateStr) => {
		if (!dateStr) return '-'
		return new Date(dateStr).toLocaleDateString('en-US', {
			month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
		})
	}

	const apiBaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://127.0.0.1:54321'

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
			{/* Section 1: Webhook Delivery */}
			<Box>
				<Typography variant="h6" fontWeight="bold" mb={2}>
					Webhook Delivery
				</Typography>
				<Typography variant="body2" color="text.secondary" mb={2}>
					These settings are managed by your administrator. Contact them to request changes.
				</Typography>

				{configLoading ? (
					<CircularProgress size={24} />
				) : customerConfig ? (
					<Paper variant="outlined" sx={{ p: 2 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
							<Typography variant="body2">
								Status: <Chip label={customerConfig.webhook_enabled ? 'Enabled' : 'Disabled'} size="small" color={customerConfig.webhook_enabled ? 'success' : 'default'} />
							</Typography>
							{customerConfig.webhook_url && (
								<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
									URL: {customerConfig.webhook_url}
								</Typography>
							)}
							<Typography variant="body2">
								Delivery: Every minute
							</Typography>
							{customerConfig.webhook_api_key && (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
									<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
										WebHook Key: ****{customerConfig.webhook_api_key.slice(-4)}
									</Typography>
									<IconButton size="small" onClick={() => handleCopy(customerConfig.webhook_api_key)}>
										<ContentCopyIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
						</Box>
					</Paper>
				) : (
					<Alert severity="warning">Could not load configuration.</Alert>
				)}
			</Box>

			{/* Section 2: REST API with API Keys and Example */}
			<Box>
				<Typography variant="h6" fontWeight="bold" mb={2}>
					REST API
				</Typography>
				<Typography variant="body2" color="text.secondary" mb={2}>
					Query historical sensor data programmatically using your API keys.
				</Typography>

				{/* API Endpoint Info */}
				<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
					<Typography variant="subtitle2" fontWeight="bold" mb={1}>
						Endpoint
					</Typography>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
						<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
							POST {apiBaseUrl}/functions/v1/customer-api
						</Typography>
						<Typography variant="body2">
							Authentication: API key (x-api-key header)
						</Typography>
						<Typography variant="body2">
							Data cap: {formatTimeLimit(max_time_range_hours)} of historical data per request
						</Typography>
					</Box>
				</Paper>

				{/* API Keys Management */}
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 3 }}>
					<Typography variant="subtitle2" fontWeight="bold">
						API Keys
					</Typography>
					<Button
						variant="contained"
						size="small"
						onClick={() => { setShowCreateForm(!showCreateForm); setNewKey(null) }}
					>
						{showCreateForm ? 'Cancel' : 'Create Key'}
					</Button>
				</Box>

				{/* New key alert */}
				{newKey && (
					<Alert
						severity="success"
						sx={{ mb: 2 }}
						action={
							<IconButton size="small" onClick={() => handleCopy(newKey)}>
								<ContentCopyIcon fontSize="small" />
							</IconButton>
						}
					>
						<Typography variant="body2" fontWeight="bold" mb={0.5}>
							{copied ? 'Copied!' : 'Your new API key (copy it now - it won\'t be shown again):'}
						</Typography>
						<Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', fontSize: '0.85rem' }}>
							{newKey}
						</Typography>
					</Alert>
				)}

				{/* Create form */}
				<Collapse in={showCreateForm}>
					<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
						<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
							<TextField
								label="Key Name"
								placeholder="e.g. Production API"
								size="small"
								value={keyName}
								onChange={(e) => setKeyName(e.target.value)}
								sx={{ minWidth: 200, flex: 1 }}
							/>
							<FormControl size="small" sx={{ minWidth: 140 }}>
								<InputLabel>Expiration</InputLabel>
								<Select
									value={keyExpiry}
									label="Expiration"
									onChange={(e) => setKeyExpiry(e.target.value)}
								>
									<MenuItem value="30d">30 days</MenuItem>
									<MenuItem value="90d">90 days</MenuItem>
									<MenuItem value="1y">1 year</MenuItem>
									<MenuItem value="never">Never</MenuItem>
								</Select>
							</FormControl>
							<Button
								variant="contained"
								onClick={handleCreateKey}
								disabled={creating || !keyName.trim()}
							>
								{creating ? <CircularProgress size={20} /> : 'Create'}
							</Button>
						</Box>
						{createError && <Alert severity="error" sx={{ mt: 1 }}>{createError}</Alert>}
					</Paper>
				</Collapse>

				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

				{loading ? (
					<CircularProgress size={24} />
				) : keys.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No API keys yet. Create one to access the REST API.
					</Typography>
				) : (
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell>Key</TableCell>
									<TableCell>Created</TableCell>
									<TableCell>Expires</TableCell>
									<TableCell>Last Used</TableCell>
									<TableCell>Status</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{keys.map((key) => {
									const status = getKeyStatus(key)
									return (
										<TableRow key={key.id}>
											<TableCell>{key.name}</TableCell>
											<TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
												{key.key_prefix}...
											</TableCell>
											<TableCell>{formatDate(key.created_at)}</TableCell>
											<TableCell>{key.expires_at ? formatDate(key.expires_at) : 'Never'}</TableCell>
											<TableCell>{formatDate(key.last_used_at)}</TableCell>
											<TableCell>
												<Chip label={status.label} size="small" color={status.color} />
											</TableCell>
											<TableCell>
												{!key.revoked_at && (
													<Button
														size="small"
														color="error"
														onClick={() => handleRevoke(key.id)}
													>
														Revoke
													</Button>
												)}
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{/* Example Usage */}
				<Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
					<Typography variant="subtitle2" fontWeight="bold" mb={1}>
						Example Request
					</Typography>
					<Box
						component="pre"
						sx={{
							fontFamily: 'monospace',
							fontSize: '0.8rem',
							bgcolor: 'action.hover',
							p: 1.5,
							borderRadius: 1,
							overflow: 'auto',
							whiteSpace: 'pre-wrap',
							wordBreak: 'break-all',
						}}
					>
{`curl -X POST ${apiBaseUrl}/functions/v1/customer-api \\
  -H "x-api-key: <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "sensor_id": "<your-sensor-id>",
    "start": "${new Date(Date.now() - 6 * 3600000).toISOString().slice(0, 19)}Z",
    "end": "${new Date().toISOString().slice(0, 19)}Z",
    "limit": 100,
    "offset": 0
  }'`}
					</Box>
					<Typography variant="body2" mt={2} mb={1} fontWeight="bold">
						Parameters:
					</Typography>
					<Box component="ul" sx={{ m: 0, pl: 2.5 }}>
						<li><Typography variant="body2"><strong>sensor_id</strong> (required) - The sensor to query</Typography></li>
						<li><Typography variant="body2"><strong>start</strong> - Start of time range (defaults to {formatTimeLimit(max_time_range_hours)} ago)</Typography></li>
						<li><Typography variant="body2"><strong>end</strong> - End of time range (defaults to now)</Typography></li>
						<li><Typography variant="body2"><strong>limit</strong> - Max results per page (1-1000, default 100)</Typography></li>
						<li><Typography variant="body2"><strong>offset</strong> - Pagination offset (default 0)</Typography></li>
					</Box>
					<Typography variant="body2" mt={2} color="text.secondary">
						Maximum time range per request: {formatTimeLimit(max_time_range_hours)}. Data is returned newest-first.
					</Typography>
				</Paper>
			</Box>
		</Box>
	)
}

export default DeveloperTab
